import i18next from 'i18next';
import { isDateValid } from './helpers/dates';
import { localizeDate } from '@pec/aion-ui-i18next/helpers/localize';

const t = i18next.t.bind(i18next);

export const required = (value: any) => (value ? undefined : i18next.t('core.validation.isRequired', 'is required'));

export const maxLength = (max: number) => (value: any) =>
  value && value.length > max
    ? i18next.t('core.validation.maxCharacters', {
        max,
        defaultValue: 'must be {{max}} characters or less'
      })
    : undefined;

export const minLength = (min: number) => (value: any) =>
  value && value.length < min
    ? i18next.t('core.validation.minCharacters', {
        min,
        defaultValue: 'must be {{min}} characters or more'
      })
    : undefined;

export const isNumber = (value: any) =>
  value && isNaN(Number(value)) ? i18next.t('core.validation.mustBeNumber', 'must be a number') : undefined;

export const isValidPhoneNumberWithMaxLength = (max: number) => (value: any) => {
  const replacedValue = value ? value.replace(/\-|#|ext|x|\.|\+|\(|\)|\s+/gi, '') : ''; //regex to check to allow special characters occurring in phone numbers
  const isRepeatedNumbers = value ? /^([0-9])\1*$/.test(replacedValue) : false; //regex to check to prevent repeated same numbers
  return value && (value.length > max || isRepeatedNumbers || isNaN(Number(+replacedValue)))
    ? i18next.t('core.validation.mustBeValidNumber', 'must be a valid phone number')
    : undefined;
};

export const isSingleAlphaCaps = (value: string) =>
  value && !/^[A-Z]{1}$/.test(value)
    ? i18next.t('core.validation.mustBeCapitalLetter', 'must be a single capital letter')
    : undefined;

export const minValue = (min: number) => (value: any) =>
  value && value < min
    ? i18next.t('core.validation.mustBeAtLeast', {
        min,
        defaultValue: 'must be at least {{min}}'
      })
    : undefined;

export const isEmail = (value: string) =>
  // General Email Regex (RFC 5322 Official Standard)
  // Source: https://emailregex.com/
  value &&
  !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i.test(
    value
  )
    ? i18next.t('core.validation.invalidEmailAddress', 'has an invalid email address')
    : undefined;

export const isHttp = (value: string) =>
  value && !/^https?:\/\//i.test(value)
    ? i18next.t('core.validation.mustBeginWith', 'must begin with http:// or https://')
    : undefined;

export const isUSAZip = (value: string) =>
  value && !/^\d{5}(-\d{4})?$/i.test(value)
    ? i18next.t('core.validation.mustBeValidZipCode', 'must be a valid US zip code')
    : undefined;

export const is4Digits = (value: string) =>
  value && !/^\d{4}$/.test(value) ? i18next.t('core.validation.mustBe4Digits', 'must be 4 digits') : undefined;

export const isPecTestId = (value: string) =>
  value && !/^[A-Z]{3}[A-Za-z0-9]{9}$/.test(value)
    ? i18next.t('core.validation.mustBeValidTestID', 'must be a valid Test ID')
    : undefined;

export const validateDate = (value: string): string | undefined =>
  isDateValid(value) ? undefined : i18next.t('core.validation.mustBeValidDate', 'must be a valid date');

export const validateName = (value: string) =>
  value && /[^a-z]/gi.test(value)
    ? i18next.t('core.validation.mustBeValidName', 'must be 2 to 50 alphabetic (A-Z) characters only')
    : undefined;

export const validatePecId = (value: string) =>
  value && !/^[-\d]\d{8}$/.test(value)
    ? i18next.t(
        'core.validation.pecNumberShouldBe',
        'PEC number should be 9 digits, or a dash (-) followed by 8 digits.'
      )
    : undefined;

export const validateDateRange = ({ start, end }: { start: string; end: string }) => {
  const isValid = new Date(start).valueOf() < new Date(end).valueOf();
  return {
    start: isValid ? undefined : i18next.t('core.validation.beforeEndDate', 'must be on or before End Date'),
    end: isValid ? undefined : i18next.t('core.validation.afterStartDate', 'must be on or after Start Date')
  };
};

export const dateIsOnOrBefore = (max: string) => (value: string) => {
  const maxDate = new Date(max);
  return new Date(value).valueOf() <= maxDate.valueOf()
    ? undefined
    : i18next.t('core.validation.mustBeOnOrBefore', {
        date: localizeDate(maxDate, t),
        defaultValue: 'must be on or before {{date}}'
      });
};

export const dateIsOnOrAfter = (min: string) => (value: string) => {
  const minDate = new Date(min);
  return new Date(value).valueOf() >= minDate.valueOf()
    ? undefined
    : i18next.t('core.validation.mustBeOnOrAfter', {
        date: localizeDate(minDate, t),
        defaultValue: 'must be on or after {{date}}'
      });
};
